/*------------------------------------------------------------------------------
 * Customer API
 * Provides interface to log in, register, log out, update details.
 */
import { reject } from 'async';
import _ from 'lodash';

const { validator } = Mobi2Go.helpers;
const { cookie } = Mobi2Go.helpers;
const { serverRequest } = Mobi2Go.app;

Mobi2Go.Customer = (function() {
    let logged_in_customer = null;
    let current_group_tab = null;

    const SAVED_EMAIL_COOKIE = 'MOBI2GO_USER_EMAIL';
    let is_logged_in = false;
    let _valid_emails = [];
    let _invalid_emails = [];

    let _valid_apple_ids = [];
    let _invalid_apple_ids = [];

    /*--------------------------------------------------------------------------
     * Events
     */
    const events = Mobi2Go.app.mixinEvents('Customer');
    const { bind } = events;
    const { trigger } = events;
    const { unbind } = events;
    const { once } = events;

    Mobi2Go.onReady(function() {
        /*----------------------------------------------------------------------
         * Set loyalty id by query string
         */
        const query_string = Mobi2Go.helpers.getQueryStringAsObject();
        if (query_string.customer_loyalty_id) {
            bind('logIn', function() {
                try {
                    update({
                        loyalty_id: query_string.customer_loyalty_id,
                    });
                } catch (e) {}
            });
        }

        /*----------------------------------------------------------------------
         * Load state
         */
        Mobi2Go.app.onStateReady(function(state) {
            if (!state.customer) {
                return;
            }

            _valid_emails.push(state.customer.email);
            setLoggedInCustomer(state.customer);
        });

        Mobi2Go.Order.onReady(function() {
            getActiveGroupTabs();
        });
    });

    /*--------------------------------------------------------------------------
     * Sign up
     */
    function create(details, callback) {
        if (is_logged_in) {
            throw new Error('cannot create account, already logged in');
        }
        validateDetails(details);

        // Ensure email is not registered before submitting to server
        isEmailRegistered(details.email, function(is_email_registered) {
            // Validate data
            if (is_email_registered) {
                throw new Error(
                    `cannot create new customer, email in use ${details.email}`
                );
            }
            serverRequest({
                api_version: 1,
                endpoint: 'customers',
                type: 'POST',
                data: details,
                success(customer) {
                    setLoggedInCustomer(customer);
                    trigger('createSuccess', customer);
                    _.isFunction(callback) && callback();
                },
            });
        });
    }

    function createAccountWithFacebook(details, callback) {
        if (is_logged_in) {
            throw new Error('cannot create account, already logged in');
        }

        serverRequest({
            api_version: 1,
            endpoint: 'customers/sso/facebook/signup',
            type: 'POST',
            data: details,
            success(customer) {
                setLoggedInCustomer(customer);
                trigger('createSuccess', customer);
                // Facebook does not provide phone number so we must request it
                trigger('requestPhoneNumber');
                _.isFunction(callback) && callback();
            },
        });
    }

    function logInWithFacebook(details, callback) {
        if (isLoggedIn()) throw new Error('cannot log in, already logged in');

        const { email } = details.customer_data;
        const accessToken = details.facebook_data.facebookAccessToken;

        if (!email) throw new Error('email required when logging in');
        if (!accessToken) throw new Error('password required logging in');

        serverRequest({
            api_version: 1,
            endpoint: 'customers/sso/facebook/signin',
            type: 'POST',
            data: details,
            error() {
                trigger('logInFail');
                if (_.isFunction(callback)) {
                    callback(false);
                }
                return true;
            },
            success(response) {
                const is_successful = !!response.id;
                if (response.id) {
                    setLoggedInCustomer(response);
                } else {
                    trigger('logInFail');
                }

                if (_.isFunction(callback)) {
                    callback(is_successful);
                }
            },
        });
    }

    function createAccountWithApple(details, callback) {
        if (is_logged_in) {
            throw new Error('cannot create account, already logged in');
        }

        serverRequest({
            api_version: 1,
            endpoint: 'customers/sso/apple/signup',
            type: 'POST',
            data: details,
            success(customer) {
                setLoggedInCustomer(customer);
                trigger('createSuccess', customer);
                // Apple does not provide phone number so we must request it
                trigger('requestPhoneNumber');
                _.isFunction(callback) && callback();
            },
        });
    }

    // When logging in with apple they only provide user object once so we must log in with apple id
    function logInWithApple(details, callback) {
        if (isLoggedIn()) throw new Error('cannot log in, already logged in');

        const { code, id_token } = details;

        if (!code) throw new Error('code required when logging in');
        if (!id_token) throw new Error('id_token required when logging in');

        serverRequest({
            api_version: 1,
            endpoint: 'customers/sso/apple/signin',
            type: 'POST',
            data: details,
            error() {
                trigger('logInFail');
                if (_.isFunction(callback)) {
                    callback(false);
                }
                return true;
            },
            success(response) {
                const is_successful = !!response.id;
                if (response.id) {
                    setLoggedInCustomer(response);
                } else {
                    trigger('logInFail');
                }

                if (_.isFunction(callback)) {
                    callback(is_successful);
                }
            },
        });
    }

    /*--------------------------------------------------------------------------
     * Check if email is in use
     */
    function isEmailRegistered(email, callback) {
        const email_in_use = true;
        const email_valid = false;

        const is_invalid = _.includes(_invalid_emails, email);
        const is_valid = _.includes(_valid_emails, email);

        if (is_valid || is_invalid) {
            if (_.isFunction(callback)) {
                _.defer(function() {
                    callback(is_invalid);
                });
            }
            return;
        }

        serverRequest({
            api_version: 1,
            endpoint: ['customers', email],
            type: 'GET',
            data: { check: true },
            success(data) {
                const { is_registered, authentication_method } = data;
                if (is_registered) {
                    _invalid_emails.push(email);
                    if (_.isFunction(callback)) {
                        callback(email_in_use, authentication_method);
                    }
                } else {
                    _valid_emails.push(email);
                    if (_.isFunction(callback)) {
                        callback(email_valid);
                    }
                }
            },
        });
    }

    /*--------------------------------------------------------------------------
     */
    function setLoggedInCustomer(customer) {
        if (customer && !(customer instanceof Mobi2Go._class.Customer)) {
            customer = Mobi2Go._class.Customer(customer);
        }
        (_valid_emails = []), (_invalid_emails = []); // Reset invalid/valid emails

        unbindLoggedInCustomerEventProxy();
        logged_in_customer = customer || null;

        if (customer) {
            is_logged_in = true;
            saveEmail(customer.email);
            trigger('logIn', customer);

            bindLoggedInCustomerEventProxy();
            if (window && window.rg4js)
                window.rg4js('setUser', {
                    identifier: customer.id,
                    isAnonymous: false,
                    email: customer.email,
                    fullName: customer.name,
                });
        } else {
            is_logged_in = false;
            trigger('logOut', customer);
        }
    }

    /*--------------------------------------------------------------------------
     * Proxy emit logged in customer events
     * This is done to prevent issues with event bindings if the
     * logged_in_customer instance changes.
     *
     * Maybe excessively complicated, could be simplier if events had better
     * namespacing
     */
    const proxy_event_handlers = _.transform(
        [
            'update',
            'updatePassword',
            'updatePasswordFail',
            'updateEmail',
            'updateEmailFail',
            'createAddress',
            'updateAddresses',
            'updateRewards',
            'updateLoyalty',
            'updateCreditBalance',
            'updateVerifiedAt',
        ],
        function(result, event) {
            result[event] = function() {
                const args = _.toArray(arguments);
                trigger.apply(null, [event].concat(args));
            };
        }
    );

    function bindLoggedInCustomerEventProxy() {
        if (logged_in_customer) {
            logged_in_customer.bind(proxy_event_handlers);
            trigger(
                'updateCreditBalance',
                logged_in_customer.getCreditBalance()
            );
        }
    }

    function unbindLoggedInCustomerEventProxy() {
        if (logged_in_customer) {
            logged_in_customer.unbind(proxy_event_handlers);
            trigger('updateCreditBalance', null);
        }
    }

    /*--------------------------------------------------------------------------
     */
    function isLoggedIn() {
        return is_logged_in;
    }

    /*--------------------------------------------------------------------------
     */
    function sendPasswordReset(email, callback) {
        if (!validator.email(email).is_valid) {
            throw new Error('Invalid email provided');
        }
        serverRequest({
            api_version: 1,
            endpoint: ['customers', email, 'password', 'reset'],
            type: 'POST',
            success(response) {
                _.isFunction(callback) && callback(response);
                trigger('passwordResetSent');
            },
        });
    }

    /*--------------------------------------------------------------------------
     */
    function logIn(email, password, callback) {
        if (isLoggedIn()) throw new Error('cannot log in, already logged in');
        if (!email) throw new Error('email required when logging in');
        if (!password) throw new Error('password required logging in');

        serverRequest({
            api_version: 1,
            endpoint: ['customers', 'session'],
            type: 'POST',
            data: { email, password },
            error() {
                trigger('logInFail');
                if (_.isFunction(callback)) {
                    callback(false);
                }
                return true;
            },
            success(response) {
                const is_successful = !!response.id;
                if (response.id) {
                    response.password = password;
                    setLoggedInCustomer(response);
                } else {
                    trigger('logInFail');
                }

                if (_.isFunction(callback)) {
                    callback(is_successful);
                }
            },
        });
    }

    /*--------------------------------------------------------------------------
     */
    function logOut(callback) {
        if (!isLoggedIn()) {
            _.isFunction(callback) && _.defer(callback);
            return;
        }

        sendLogOutRequest(function() {
            setLoggedInCustomer(null);
            _.isFunction(callback) && callback();
        });
    }

    /*--------------------------------------------------------------------------
     */
    function sendLogOutRequest(callback) {
        serverRequest({
            api_version: 1,
            endpoint: ['customers', 'session'],
            type: 'DELETE',
            complete: callback,
        });
    }

    /*--------------------------------------------------------------------------
     */
    function get() {
        return logged_in_customer;
    }
    function getId() {
        return logged_in_customer && logged_in_customer.id;
    }
    function getEmail() {
        return logged_in_customer && logged_in_customer.email;
    }
    function getName() {
        return logged_in_customer && logged_in_customer.name;
    }
    function getPhoneNumber() {
        return logged_in_customer && logged_in_customer.phone_number;
    }
    function getLoyaltyId() {
        return logged_in_customer && logged_in_customer.loyalty_id;
    }
    function isGuest() {
        return logged_in_customer && logged_in_customer.is_guest;
    }
    function isSubscriber() {
        return logged_in_customer && logged_in_customer.is_subscriber;
    }
    function refresh(callback) {
        return logged_in_customer && logged_in_customer.refresh(callback);
    }

    function getVerifiedAt() {
        return logged_in_customer && logged_in_customer.getVerifiedAt();
    }

    function getRewards() {
        return logged_in_customer && logged_in_customer.getRewards();
    }

    function getLoyalty(scheme) {
        return logged_in_customer && logged_in_customer.getLoyalty(scheme);
    }

    function getActiveLoyaltyProgram() {
        return (
            logged_in_customer && logged_in_customer.getActiveLoyaltyProgram()
        );
    }

    function getActiveLoyaltyPrograms() {
        return (
            logged_in_customer && logged_in_customer.getActiveLoyaltyPrograms()
        );
    }

    function getDateOfBirth() {
        return logged_in_customer && logged_in_customer.getDateOfBirth();
    }

    function getGender() {
        return logged_in_customer && logged_in_customer.getGender();
    }

    function getDietaryPreference() {
        return logged_in_customer && logged_in_customer.getDietaryPreference();
    }

    function getFavouriteLocation() {
        return logged_in_customer && logged_in_customer.getFavouriteLocation();
    }

    function getPostCode() {
        return logged_in_customer && logged_in_customer.getPostCode();
    }

    function getLanguage() {
        return logged_in_customer && logged_in_customer.getLanguage();
    }

    function shouldPromptSMSVerification() {
        return (
            logged_in_customer &&
            logged_in_customer.shouldPromptSMSVerification()
        );
    }

    function shouldPromptToJoinLoyaltyClub() {
        return (
            logged_in_customer &&
            logged_in_customer.shouldPromptToJoinLoyaltyClub()
        );
    }

    function isInLoyaltyClub() {
        return logged_in_customer && logged_in_customer.isInLoyaltyClub();
    }

    function providePassword(password, callback) {
        return (
            logged_in_customer &&
            logged_in_customer.providePassword(password, callback)
        );
    }

    function provideFacebookAuthentication(authorisation, callback) {
        return (
            logged_in_customer &&
            logged_in_customer.provideFacebookAuthentication(
                authorisation,
                callback
            )
        );
    }

    function provideAppleAuthentication(authorisation, callback) {
        return (
            logged_in_customer &&
            logged_in_customer.provideAppleAuthentication(
                authorisation,
                callback
            )
        );
    }

    function isPasswordProvided() {
        return !!(
            logged_in_customer && logged_in_customer.isPasswordProvided()
        );
    }

    /*--------------------------------------------------------------------------
     */
    function update(properties, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        logged_in_customer.update(properties, callback);
    }

    /**-------------------------------------------------------------------------
     */
    function updateWhenLoggedIn(properties, callback) {
        if (isLoggedIn()) {
            logged_in_customer.update(properties, callback);
        } else {
            if (updateWhenLoggedIn.callback) {
                unbind('logIn', updateWhenLoggedIn.callback);
            }

            updateWhenLoggedIn.callback = function() {
                logged_in_customer.update(properties, callback);
            };

            bind('logIn', updateWhenLoggedIn.callback);
        }
    }

    /*--------------------------------------------------------------------------
     */
    function updatePassword(password, new_password, callback) {
        if (!isLoggedIn()) throw new Error('must be logged in');

        logged_in_customer.updatePassword(password, new_password, callback);
    }

    /*--------------------------------------------------------------------------
     */
    function updateEmail(email, password, callback) {
        if (!isLoggedIn()) throw new Error('must be logged in');

        logged_in_customer.updateEmail(email, password, callback);
        saveEmail(getEmail());
    }

    /*--------------------------------------------------------------------------
     * Addresses
     */

    function getAddressById(id) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getAddressById(id);
    }

    function getAddresses() {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getAddresses();
    }

    function createAddress(properties, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.createAddress(properties, callback);
    }

    function updateAddressById(id, properties, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.updateAddressById(id, properties, callback);
    }

    function removeAddressById(id, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.removeAddressById(id, callback);
    }

    /**-------------------------------------------------------------------------
     * Payment Card Tokens
     */
    function removePaymentCardToken(token, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.removePaymentCardToken(token, callback);
    }

    function getGiftCard(success, error) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getGiftCard(success, error);
    }

    function addGiftCard(query, success, error) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.addGiftCard(query, success, error);
    }

    function removeGiftCard(success, error) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.removeGiftCard(success, error);
    }

    function requestVerification() {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.requestVerification();
    }

    function completeVerification(code) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.completeVerification(code);
    }

    /*--------------------------------------------------------------------------
     * Orders
     */
    function getOrders(query, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getOrders(query, callback);
    }

    /*--------------------------------------------------------------------------
     */
    function getCreditBalance() {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getCreditBalance();
    }

    /*--------------------------------------------------------------------------
     * Payment Card Tokens
     */
    function getPaymentCardTokensForLocation(location, callback) {
        if (!isLoggedIn()) {
            throw new Error('must be logged in');
        }

        return logged_in_customer.getPaymentCardTokensForLocation(
            location,
            callback
        );
    }

    /*--------------------------------------------------------------------------
     */
    function getSavedEmail() {
        return cookie.get(SAVED_EMAIL_COOKIE);
    }

    /*--------------------------------------------------------------------------
     */
    function saveEmail(email) {
        cookie.set(SAVED_EMAIL_COOKIE, email, 60 * 60 * 24 * 24);
    }

    /*--------------------------------------------------------------------------
     */
    function clearSavedEmail() {
        cookie.set(SAVED_EMAIL_COOKIE, '');
    }

    /*--------------------------------------------------------------------------
     * Group Tabs
     */
    function getActiveGroupTabs() {
        return new Promise((resolve, reject) => {
            serverRequest({
                api_version: 1,
                endpoint: ['customers', 'session', 'group_tabs'],
                type: 'GET',
                success: response => {
                    if (response.length) {
                        setCurrentGroupTab(response[0]);
                        trigger('groupTabSuccess', response);
                    } else {
                        setCurrentGroupTab(null);
                    }
                    resolve();
                },
                error: message => {
                    reject(message);
                },
            });
        });
    }

    function createGroupTab(
        locationId,
        phoneNumber,
        initialHoldAmount,
        isSandbox
    ) {
        return new Promise((resolve, reject) => {
            serverRequest({
                api_version: 1,
                endpoint: ['customers', 'session', 'group_tabs'],
                type: 'POST',
                data: {
                    location_id: locationId,
                    phone_number: phoneNumber,
                    initial_hold_amount: initialHoldAmount,
                    sandbox: isSandbox,
                },
                success: response => {
                    setCurrentGroupTab(response);
                    trigger('createGroupTabSuccess');
                    resolve(response);
                },
                error: message => {
                    reject(message);
                },
            });
        });
    }

    function increaseGroupTab(holdAmount) {
        const currentGroupTab = getCurrentGroupTab();
        return new Promise((resolve, reject) => {
            if (!currentGroupTab) {
                reject('No group tab to increase');
            }
            serverRequest({
                api_version: 1,
                endpoint: [
                    'customers',
                    'session',
                    'group_tabs',
                    currentGroupTab.id,
                    'card_holds',
                ],
                type: 'POST',
                data: {
                    hold_amount: holdAmount,
                },
                success: response => {
                    trigger('increaseGroupTabSuccess');
                    getActiveGroupTabs();
                    resolve(response);
                },
                error: message => {
                    reject(message);
                },
            });
        });
    }

    function closeGroupTab() {
        const currentGroupTab = getCurrentGroupTab();
        return new Promise((resolve, reject) => {
            if (!currentGroupTab) {
                reject('No group tab to close');
            }

            if (!currentGroupTab.owned_by_session) {
                reject('Only tab owners can close group tabs');
            }
            serverRequest({
                api_version: 1,
                endpoint: [
                    'customers',
                    'session',
                    'group_tabs',
                    currentGroupTab.id,
                ],
                type: 'DELETE',
                success: () => {
                    trigger('closeGroupTabSuccess');
                    resolve();
                },
                error: message => {
                    reject(message);
                },
            });
        });
    }

    function joinGroupTab(joinCode) {
        return new Promise((resolve, reject) => {
            serverRequest({
                api_version: 1,
                endpoint: ['customers', 'session', 'group_tabs', 'join'],
                type: 'POST',
                data: {
                    join_code: joinCode,
                },
                success: response => {
                    setCurrentGroupTab(response);
                    trigger('joinGroupTabSuccess', response);
                    resolve(response);
                },
                error: message => {
                    reject(message);
                    return true;
                },
            });
        });
    }

    function setCurrentGroupTab(tab) {
        current_group_tab = tab;
    }

    function getCurrentGroupTab() {
        return current_group_tab;
    }

    /*--------------------------------------------------------------------------
     * Public methods
     */
    return {
        // Logged out
        create,
        createAccountWithFacebook,
        logInWithFacebook,
        createAccountWithApple,
        logInWithApple,
        isEmailRegistered,
        logIn,
        isLoggedIn,
        sendPasswordReset,

        // Logged in
        logOut,
        updatePassword,
        updateEmail,
        update,
        refresh,
        updateWhenLoggedIn,
        get,
        getEmail,
        getName,
        getLoyaltyId,
        getPhoneNumber,
        getRewards,
        getLoyalty,
        getCreditBalance,
        getActiveLoyaltyProgram,
        getActiveLoyaltyPrograms,
        getDateOfBirth,
        getGender,
        getDietaryPreference,
        getFavouriteLocation,
        getPostCode,
        getLanguage,
        isGuest,
        isSubscriber,
        shouldPromptSMSVerification,
        shouldPromptToJoinLoyaltyClub,
        isInLoyaltyClub,
        providePassword,
        provideFacebookAuthentication,
        provideAppleAuthentication,
        isPasswordProvided,

        getSavedEmail,
        saveEmail,
        clearSavedEmail,

        getAddresses,
        createAddress,
        getAddressById,

        updateAddressById,
        removeAddressById,
        removePaymentCardToken,

        getOrders,
        _sendLogOutRequest: sendLogOutRequest,
        _setLoggedInCustomer: setLoggedInCustomer,

        bind,
        trigger,
        unbind,
        once,

        getGiftCard,
        addGiftCard,
        removeGiftCard,

        getVerifiedAt,
        requestVerification,
        completeVerification,

        getActiveGroupTabs,
        createGroupTab,
        increaseGroupTab,
        closeGroupTab,
        joinGroupTab,
        setCurrentGroupTab,
        getCurrentGroupTab,
    };
})();

function validateDetails(details) {
    if (!details.email || !validator.email(details.email).is_valid) {
        throw new Error('valid email required to create customer');
    }
    if (!details.name) {
        throw new Error('name required to create customer');
    }
    if (
        !details.phone_number ||
        (details.phone_number !== '000000000000' &&
            !validator.phoneNumber(details.phone_number).is_valid)
    ) {
        throw new Error('valid phone number required to create customer');
    }
    if (
        (!Mobi2Go.headoffice.customer_guest_checkout_enabled &&
            !details.password) ||
        (details.password && !validator.password(details.password).is_valid)
    ) {
        throw new Error('valid password required to create customer');
    }

    details.is_subscriber = !!details.is_subscriber;
}
